import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Receipt,
  Calendar,
  Store,
  Tag,
  X,
  Home,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { receiptsApi, tagsApi, categoriesApi } from '../api/receipts';
import { householdsApi } from '../api/households';
import type {
  Receipt as ReceiptType,
  ReceiptFilters,
  PaginatedResponse,
  Tag as TagType,
  Category,
  Household,
} from '../types';
import { toFixed } from '../utils/format';

// Haushalt-Scope: 'mine' = nur eigene, 'household' = nur Haushalt, 'all' = beides
type HouseholdScope = 'mine' | 'household' | 'all';

// =============================================
// Status Badge
// =============================================
const statusLabels: Record<string, { label: string; className: string }> = {
  pending: { label: 'Wird verarbeitet...', className: 'bg-warning-50 text-warning-700' },
  processing: { label: 'Wird erkannt...', className: 'bg-info-50 text-info-700' },
  ocr_done: { label: 'Erkannt', className: 'bg-secondary-50 text-secondary-600' },
  reviewed: { label: 'Geprüft', className: 'bg-primary-50 text-primary-600' },
  completed: { label: 'Fertig', className: 'bg-success-50 text-success-700' },
  error: { label: 'Fehler', className: 'bg-error-50 text-error-700' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = statusLabels[status] || { label: status, className: 'bg-neutral-100 text-neutral-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

// =============================================
// Receipt Card
// =============================================
const ReceiptCard: React.FC<{ receipt: ReceiptType; onClick: () => void; currentUserId?: string }> = ({
  receipt,
  onClick,
  currentUserId,
}) => (
  <Card hover onClick={onClick} className="group">
    <div className="flex items-start gap-4">
      {/* Icon */}
      <div className="w-12 h-12 bg-primary-50 text-primary-500 rounded-xl flex items-center justify-center shrink-0
                      group-hover:bg-primary-100 transition-colors">
        <Receipt className="w-6 h-6" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
                {receipt.store?.name || receipt.store_name || 'Unbekannter Laden'}
              </h3>
              {currentUserId && receipt.user_id && receipt.user_id !== currentUserId && (
                <span
                  title="Von Partner hochgeladen"
                  className="w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400
                             flex items-center justify-center text-xs font-bold shrink-0"
                >
                  P
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-neutral-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {receipt.receipt_date
                  ? new Date(receipt.receipt_date).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })
                  : 'Kein Datum'}
              </span>
              {receipt.item_count != null && (
                <span className="text-xs text-neutral-400">
                  {receipt.item_count} Artikel
                </span>
              )}
            </div>
          </div>

          {/* Amount */}
          <div className="text-right shrink-0">
            <p className="text-base font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100">
              {receipt.total_amount != null
                ? `${toFixed(receipt.total_amount, 2)}`
                : '--,--'}
            </p>
            <p className="text-xs text-neutral-400">{receipt.currency || 'EUR'}</p>
          </div>
        </div>

        {/* Tags + Status */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <StatusBadge status={receipt.status} />
          {receipt.tags?.slice(0, 3).map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
                         bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 font-medium"
            >
              <Tag className="w-3 h-3" />
              {tag.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  </Card>
);

// =============================================
// Receipt List Page
// =============================================
export const ReceiptListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAuthStore((s) => s.user);

  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    searchParams.getAll('tag_ids')
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    searchParams.get('category_id') || ''
  );
  const [householdScope, setHouseholdScope] = useState<HouseholdScope>(
    (searchParams.get('scope') as HouseholdScope) || 'all'
  );

  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Haushalt laden (für Scope-Toggle)
  const { data: households } = useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: householdsApi.list,
  });
  const household = households && households.length > 0 ? households[0] : null;

  // household_id aus Scope ableiten
  const householdIdFilter: string | undefined =
    household && householdScope === 'household'
      ? household.id
      : household && householdScope === 'all'
      ? household.id
      : undefined;

  const filters: ReceiptFilters = {
    page: currentPage,
    page_size: 20,
    search: searchParams.get('search') || undefined,
    date_from: searchParams.get('date_from') || undefined,
    date_to: searchParams.get('date_to') || undefined,
    tag_ids: searchParams.getAll('tag_ids').length > 0
      ? searchParams.getAll('tag_ids')
      : undefined,
    category_id: searchParams.get('category_id') || undefined,
    household_id: householdIdFilter,
  };

  const { data, isLoading, isError } = useQuery<PaginatedResponse<ReceiptType>>({
    queryKey: ['receipts', filters],
    queryFn: () => receiptsApi.list(filters),
  });

  const { data: allTags } = useQuery<TagType[]>({
    queryKey: ['tags'],
    queryFn: tagsApi.list,
    enabled: showFilters,
  });

  const { data: allCategories } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
    enabled: showFilters,
  });

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (selectedCategoryId) params.set('category_id', selectedCategoryId);
    selectedTagIds.forEach((id) => params.append('tag_ids', id));
    if (householdScope !== 'all') params.set('scope', householdScope);
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setSelectedTagIds([]);
    setSelectedCategoryId('');
    setHouseholdScope('all');
    setSearchParams({});
  };

  const handleScopeChange = (scope: HouseholdScope) => {
    setHouseholdScope(scope);
    const params = new URLSearchParams(searchParams);
    if (scope !== 'all') {
      params.set('scope', scope);
    } else {
      params.delete('scope');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const goToPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const receipts = data?.items || [];
  const totalPages = data?.pages || 0;
  const totalItems = data?.total || 0;
  const hasActiveFilters =
    searchTerm ||
    dateFrom ||
    dateTo ||
    selectedTagIds.length > 0 ||
    selectedCategoryId ||
    householdScope !== 'all';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800 dark:text-white">Kassenzettel</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {data
              ? `${totalItems} Belege gefunden`
              : 'Deine Kassenzettel'}
          </p>
        </div>
        <Button onClick={() => navigate('/receipts/new')}>
          Neuer Beleg
        </Button>
      </div>

      {/* Search + Filter Bar */}
      <Card padding="sm">
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Suche nach Geschäft, Artikel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                           bg-white dark:bg-[#1e1e2e]/60 dark:text-white
                           placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                           focus:outline-none focus:ring-2
                           focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters || hasActiveFilters
                ? '!border-primary-400 !text-primary-600 dark:!border-primary-500 dark:!text-primary-400'
                : ''}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Filter</span>
              {hasActiveFilters && (
                <span className="ml-1.5 w-5 h-5 rounded-full bg-primary-500 text-white text-xs flex items-center justify-center font-bold">
                  {[
                    searchTerm ? 1 : 0,
                    dateFrom || dateTo ? 1 : 0,
                    selectedTagIds.length > 0 ? 1 : 0,
                    selectedCategoryId ? 1 : 0,
                  ].reduce((a, b) => a + b, 0)}
                </span>
              )}
            </Button>
            <Button onClick={handleSearch}>Suchen</Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="flex flex-col gap-4 pt-3 border-t border-neutral-200 dark:border-indigo-500/10">
              {/* Haushalt-Scope Toggle — nur wenn User in einem Haushalt ist */}
              {household && (
                <div className="flex items-center gap-3">
                  <label className="text-xs text-neutral-500 dark:text-neutral-400 font-medium whitespace-nowrap flex items-center gap-1">
                    <Home className="w-3.5 h-3.5" />
                    Ansicht:
                  </label>
                  <div className="flex rounded-lg border border-neutral-300 dark:border-indigo-500/20 overflow-hidden">
                    {(['mine', 'household', 'all'] as HouseholdScope[]).map((scope) => {
                      const labels: Record<HouseholdScope, string> = {
                        mine: 'Meine',
                        household: 'Haushalt',
                        all: 'Alle',
                      };
                      return (
                        <button
                          key={scope}
                          onClick={() => handleScopeChange(scope)}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors
                            ${householdScope === scope
                              ? 'bg-primary-500 text-white'
                              : 'bg-white dark:bg-[#1e1e2e]/60 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                            }`}
                        >
                          {labels[scope]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Date range */}
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-neutral-500 dark:text-neutral-400 font-medium whitespace-nowrap">
                    Von:
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                               bg-white dark:bg-[#1e1e2e]/60 dark:text-white
                               focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-neutral-500 dark:text-neutral-400 font-medium whitespace-nowrap">
                    Bis:
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                               bg-white dark:bg-[#1e1e2e]/60 dark:text-white
                               focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>
              </div>

              {/* Category filter */}
              <div className="flex items-center gap-3">
                <label className="text-xs text-neutral-500 dark:text-neutral-400 font-medium whitespace-nowrap">
                  Kategorie:
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="flex-1 sm:max-w-xs px-3 py-1.5 text-sm border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                             bg-white dark:bg-[#1e1e2e]/60 dark:text-white
                             focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="">Alle Kategorien</option>
                  {(allCategories || []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tag filter — Multi-Select Pills */}
              {allTags && allTags.length > 0 && (
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium mb-2">
                    Tags:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTagFilter(tag.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                                      transition-colors border
                                      ${isSelected
                                        ? 'bg-primary-500 text-white border-primary-500 dark:bg-primary-600 dark:border-primary-600'
                                        : 'bg-white dark:bg-[#1e1e2e]/60 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-indigo-500/20 hover:border-primary-400'
                                      }`}
                        >
                          <Tag className="w-3 h-3" />
                          {tag.name}
                          {isSelected && <X className="w-3 h-3 ml-0.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reset button */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="self-start flex items-center gap-1 px-3 py-1.5 text-xs text-error-500 hover:bg-error-50
                             rounded-lg transition-colors font-medium"
                >
                  <X className="w-3 h-3" />
                  Alle Filter zurücksetzen
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Active filter pills (visible even when filter panel is closed) */}
      {!showFilters && hasActiveFilters && (
        <div className="flex flex-wrap gap-2 -mt-3">
          {household && householdScope !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                             bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300">
              <Home className="w-3 h-3" />
              {householdScope === 'mine' ? 'Nur meine' : 'Haushalt'}
            </span>
          )}
          {searchTerm && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                             bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
              Suche: {searchTerm}
            </span>
          )}
          {(dateFrom || dateTo) && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                             bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
              <Calendar className="w-3 h-3" />
              {dateFrom && dateTo ? `${dateFrom} – ${dateTo}` : dateFrom || dateTo}
            </span>
          )}
          {selectedCategoryId && allCategories && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                             bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
              {allCategories.find((c) => c.id === selectedCategoryId)?.name || 'Kategorie'}
            </span>
          )}
          {selectedTagIds.map((tid) => {
            const tag = (allTags || []).find((t) => t.id === tid);
            return tag ? (
              <span
                key={tid}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                           bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300"
              >
                <Tag className="w-3 h-3" />
                {tag.name}
              </span>
            ) : null;
          })}
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                       text-error-500 hover:bg-error-50 transition-colors"
          >
            <X className="w-3 h-3" />
            Zurücksetzen
          </button>
        </div>
      )}

      {/* Receipt List */}
      {isLoading ? (
        <LoadingSpinner fullPage text="Belege werden geladen..." />
      ) : isError ? (
        <Card><p className="text-center py-8 text-error-500">Daten konnten nicht geladen werden. Bitte versuche es erneut.</p></Card>
      ) : receipts.length > 0 ? (
        <div className="space-y-3">
          {receipts.map((receipt) => (
            <ReceiptCard
              key={receipt.id}
              receipt={receipt}
              onClick={() => navigate(`/receipts/${receipt.id}`)}
              currentUserId={currentUser?.id}
            />
          ))}
        </div>
      ) : (
        <Card padding="lg">
          <div className="text-center py-12">
            <Store className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
            <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-200 mb-1">
              Keine Belege gefunden
            </h3>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mb-4">
              {hasActiveFilters
                ? 'Versuche andere Suchbegriffe oder Filter.'
                : 'Lade deinen ersten Kassenzettel hoch.'}
            </p>
            {!hasActiveFilters && (
              <Button onClick={() => navigate('/receipts/new')}>
                Ersten Beleg hochladen
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            let page: number;
            if (totalPages <= 7) {
              page = i + 1;
            } else if (currentPage <= 4) {
              page = i + 1;
            } else if (currentPage >= totalPages - 3) {
              page = totalPages - 6 + i;
            } else {
              page = currentPage - 3 + i;
            }
            return (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`w-9 h-9 text-sm rounded-lg font-medium transition-colors ${
                  page === currentPage
                    ? 'bg-primary-500 text-white'
                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10'
                }`}
              >
                {page}
              </button>
            );
          })}

          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
